import * as React from 'react'
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '../../../lib/utils'
import { useDebugMode } from '../../../lib/debug-mode'
import {
  useActiveBranchRecord,
  useApplyStructuredDiffMutation,
  useDiscardStructuredDiffMutation,
  useGetIpMappingJobDetailQuery,
  useGetJobDiffPreviewQuery,
  useGetMTConnectJobDetailQuery,
  useGetPIJobDetailQuery,
  useGetCanaryJobDetailQuery,
  useGetExperionJobDetailQuery,
  useGetSrsJobDetailQuery,
  useGetSafetyManagerJobDetailQuery,
  useGetTagMappingJobDetailQuery,
  useGetWorkOrdersJobDetailQuery,
  useGetPlcJobDetailQuery,
  useGetRoboticsJobDetailQuery,
  useGetVisionJobDetailQuery,
  useGetStructuredJobDetailQuery,
  useGetUnstructuredJobDetailQuery,
  useGetTagImportJobDetailQuery,
  useGetTabularDumpJobDetailQuery,
  useGetSqlJobDetailQuery,
  useGetSoapJobDetailQuery,
  useGetPssJobDetailQuery,
  useLazyGetTagImportErrorCsvQuery,
  type TagImportJob,
  type TagImportPreview,
  type TabularDumpJob,
  type TabularDumpTablePreview
} from '@axilon-apps/domains-ontology-ui-state'
import { useProtectedBranchGuard } from '../../../components/Branches/ProtectedBranchPromptDialog'
import { Button } from '../../../components/ui/button'
import { StatusPill } from '../../../components/primitives/StatusPill'
import { JobLogs } from '../../../components/JobLogs/JobLogs'
import { statusToLabel, statusToPill, type IngestJobState } from '../util/jobStatusMap'
import type { IngestJobKind } from '../util/mergeIngestJobs'
import {
  INGEST_SESSION_FOCUS_STATE,
  ingestFullSessionPath
} from '../../../components/IngestSession/ingest-session-route'
import { DiffResultView } from './DiffResultView'
import { extractErrorMessage } from '../util/extractErrorMessage'

interface JobDetailPanelProps {
  kind: IngestJobKind
  id: string
}

function downloadTagImportErrors(csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = 'tag-import-errors.csv'
  anchor.click()
  URL.revokeObjectURL(url)
}

// What to change, per reason a Direct OPC endpoint could not be read. The
// message from the server names the endpoint and what happened; this names the
// fix, so the operator is not left inferring it from a row count of zero.
const VERIFICATION_FIXES: Record<
  NonNullable<TagImportPreview['verificationFailure']>['reason'],
  string
> = {
  authentication:
    'Check the OPC UA username and password for this endpoint, then upload the tag list again.',
  namespace:
    'Check the namespace URI in the opc_node_id column against the namespaces the server publishes, then upload the tag list again.',
  unreachable:
    'Check the endpoint URL and that the OPC UA server is reachable, then upload the tag list again.'
}

/**
 * Says whether the preview's rows were actually read.
 *
 * A preview that shows only counts reads as success even when nothing could be
 * read, which is how a failed verification looked before: a row count implying
 * an import that had in fact registered nothing.
 */
function VerificationNotice({ preview }: { preview: TagImportPreview }) {
  const failure = preview.verificationFailure
  if (failure) {
    return (
      <div
        role="alert"
        className="mt-3 rounded-md border border-st-down/30 bg-st-down-bg px-3 py-2 text-xs text-st-down"
      >
        <div className="font-semibold">No tags were registered</div>
        <div className="mt-1 text-st-down/90">{failure.message}</div>
        <div className="mt-1 text-st-down/90">{VERIFICATION_FIXES[failure.reason]}</div>
      </div>
    )
  }
  if (preview.verificationStatus === 'gateway_offline') {
    return (
      <div
        role="status"
        className="mt-3 rounded-md border border-st-warn/30 bg-st-warn-bg px-3 py-2 text-xs text-st-warn"
      >
        The gateway did not answer, so these rows were registered without being spot-read.
        Acquisition will report their quality once it responds.
      </div>
    )
  }
  // A row that resolved but has no value yet is a fact about the machine, not
  // the file. It registers, so the count above is not wrong — but saying
  // nothing would leave "Ready: 2395" looking like every tag is live.
  const unread = preview.sampleReads.filter((read) => !read.quality.startsWith('Good'))
  if (unread.length > 0) {
    return (
      <div
        role="status"
        className="mt-3 rounded-md border border-st-warn/30 bg-st-warn-bg px-3 py-2 text-xs text-st-warn"
      >
        {unread.length} of {preview.sampleReads.length} spot-read rows had no value yet. They were
        registered anyway, because their addresses resolved — acquisition will report values once
        the device answers.
      </div>
    )
  }
  return null
}

function TagImportPreviewPanel({
  preview,
  onDownloadErrors
}: {
  preview: TagImportPreview
  onDownloadErrors: () => void
}) {
  return (
    <div className="rounded-md border border-line bg-bg-elev p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-semibold text-ink">Tag import preview</div>
      </div>
      <VerificationNotice preview={preview} />
      <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        {[
          ['Rows', preview.totalRows],
          ['Ready', preview.valid],
          ['Invalid', preview.invalid],
          ['Existing', preview.existing],
          ['New', preview.new],
          ['Enabled', preview.enabledCount]
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded border border-line bg-bg-sunk px-2 py-1.5">
            <div className="text-ink-3">{label}</div>
            <div className="mt-0.5 font-semibold text-ink">{value}</div>
          </div>
        ))}
      </div>
      <PreviewCounts title="By OPC server" counts={preview.byServer} />
      <PreviewCounts title="By category" counts={preview.byCategory} />
      {preview.sampleReads.length > 0 ? (
        <div className="mt-3">
          <div className="text-xs font-semibold text-ink-2">Sample reads</div>
          <div className="mt-1 max-h-36 overflow-auto rounded border border-line">
            {preview.sampleReads.map((read) => (
              <div
                key={read.path}
                className="grid grid-cols-[1fr_auto_auto] gap-2 border-b border-line px-2 py-1 text-11 last:border-b-0"
              >
                <span className="truncate font-mono text-ink-3">{read.path}</span>
                <span className="max-w-32 truncate text-ink-2">{read.value ?? '—'}</span>
                <span className="text-ink-3">{read.quality}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {preview.errors.length > 0 ? (
        <div className="mt-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-semibold text-ink-2">
              Validation messages ({preview.errors.length})
            </div>
            <Button size="sm" variant="outline" onClick={onDownloadErrors}>
              Download error CSV
            </Button>
          </div>
          <div className="mt-1 max-h-44 overflow-auto rounded border border-line">
            {preview.errors.map((error, index) => (
              <div
                key={`${error.row}-${error.column ?? ''}-${index}`}
                className="grid grid-cols-[auto_auto_1fr] gap-2 border-b border-line px-2 py-1 text-11 last:border-b-0"
              >
                <span className="text-ink-3">Row {error.row}</span>
                <span className={error.severity === 'error' ? 'text-st-down' : 'text-st-warn'}>
                  {error.column ?? 'row'}
                </span>
                <span className="text-ink-2">{error.message}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function PreviewCounts({ title, counts }: { title: string; counts: Record<string, number> }) {
  const entries = Object.entries(counts)
  if (entries.length === 0) return null
  return (
    <div className="mt-3">
      <div className="text-xs font-semibold text-ink-2">{title}</div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {entries.map(([label, count]) => (
          <span key={label} className="rounded bg-bg-sunk px-2 py-1 text-11 text-ink-2">
            {label}: {count}
          </span>
        ))}
      </div>
    </div>
  )
}

function DumpPreviewPanel({ tables }: { tables: TabularDumpTablePreview[] }) {
  return (
    <div className="rounded-md border border-line bg-bg-elev p-3">
      <div className="text-xs font-semibold text-ink">Dump tables</div>
      <div className="mt-2 max-h-36 overflow-auto rounded border border-line">
        {tables.map((table) => (
          <div
            key={table.tableName}
            className="grid grid-cols-[1fr_auto] gap-2 border-b border-line px-2 py-1 text-11 last:border-b-0"
          >
            <span className="truncate font-mono text-ink-3">{table.tableName}</span>
            <span className="text-ink-3">
              {table.rowCount} row{table.rowCount === 1 ? '' : 's'}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function JobDetailPanel({ kind, id }: JobDetailPanelProps) {
  const debugMode = useDebugMode()
  const isScada = kind === 'SCADA'
  const isPlc = kind === 'PLC'
  const isUpdatePlc = kind === 'UpdatePLC'
  const isRobotics = kind === 'Robotics'
  const isVision = kind === 'Vision'
  const isMTConnect = kind === 'MTConnect'
  const isPI = kind === 'PI'
  const isCanary = kind === 'Canary'
  const isExperion = kind === 'Experion'
  const isSrs = kind === 'SRS'
  const isSafetyManager = kind === 'SafetyManager'
  const isTagMapping = kind === 'TagMapping'
  const isUnstructured = kind === 'Unstructured'
  const isStructuredDocument = kind === 'StructuredDocument'
  const isTagImport = kind === 'TagImport'
  const isTabularDump = kind === 'TabularDump'
  const isSql = kind === 'Sql'
  const isSoap = kind === 'SOAP'
  const isPss = kind === 'PSS'
  const isWorkOrders = kind === 'WorkOrders'

  const structuredQ = useGetStructuredJobDetailQuery(id, {
    skip: !isScada,
    refetchOnMountOrArgChange: true
  })
  const plcQ = useGetPlcJobDetailQuery(id, {
    skip: !(isPlc || isUpdatePlc),
    refetchOnMountOrArgChange: true
  })
  const roboticsQ = useGetRoboticsJobDetailQuery(id, {
    skip: !isRobotics,
    refetchOnMountOrArgChange: true
  })
  const visionQ = useGetVisionJobDetailQuery(id, {
    skip: !isVision,
    refetchOnMountOrArgChange: true
  })
  const mtconnectQ = useGetMTConnectJobDetailQuery(id, {
    skip: !isMTConnect,
    refetchOnMountOrArgChange: true
  })
  const piQ = useGetPIJobDetailQuery(id, {
    skip: !isPI,
    refetchOnMountOrArgChange: true
  })
  const canaryQ = useGetCanaryJobDetailQuery(id, {
    skip: !isCanary,
    refetchOnMountOrArgChange: true
  })
  const experionQ = useGetExperionJobDetailQuery(id, {
    skip: !isExperion,
    refetchOnMountOrArgChange: true
  })
  const srsQ = useGetSrsJobDetailQuery(id, {
    skip: !isSrs,
    refetchOnMountOrArgChange: true
  })
  const safetyManagerQ = useGetSafetyManagerJobDetailQuery(id, {
    skip: !isSafetyManager,
    refetchOnMountOrArgChange: true
  })
  const tagMappingQ = useGetTagMappingJobDetailQuery(id, {
    skip: !isTagMapping,
    refetchOnMountOrArgChange: true
  })
  const unstructuredQ = useGetUnstructuredJobDetailQuery(id, {
    skip: !isUnstructured,
    refetchOnMountOrArgChange: true
  })
  // IP-mapping jobs hit the same generic /jobs/:id endpoint but cache
  // under the `IpMappingJobs` tag so the job detail is invalidated
  // when the upload finishes / the user clicks Stop.
  const ipMappingQ = useGetIpMappingJobDetailQuery(id, {
    skip: !isStructuredDocument,
    refetchOnMountOrArgChange: true
  })
  const workOrdersQ = useGetWorkOrdersJobDetailQuery(id, {
    skip: !isWorkOrders,
    refetchOnMountOrArgChange: true
  })
  const tagImportQ = useGetTagImportJobDetailQuery(id, {
    skip: !isTagImport,
    refetchOnMountOrArgChange: true
  })
  const tabularDumpQ = useGetTabularDumpJobDetailQuery(id, {
    skip: !isTabularDump,
    refetchOnMountOrArgChange: true
  })
  const sqlQ = useGetSqlJobDetailQuery(id, {
    skip: !isSql,
    refetchOnMountOrArgChange: true
  })
  const soapQ = useGetSoapJobDetailQuery(id, {
    skip: !isSoap,
    refetchOnMountOrArgChange: true
  })
  const pssQ = useGetPssJobDetailQuery(id, {
    skip: !isPss,
    refetchOnMountOrArgChange: true
  })
  const job = isScada
    ? structuredQ.data
    : isPlc || isUpdatePlc
      ? plcQ.data
      : isRobotics
        ? roboticsQ.data
        : isVision
          ? visionQ.data
          : isMTConnect
            ? mtconnectQ.data
            : isPI
              ? piQ.data
              : isExperion
                ? experionQ.data
                : isSrs
                  ? srsQ.data
                  : isSafetyManager
                    ? safetyManagerQ.data
                    : isTagMapping
                      ? tagMappingQ.data
                      : isUnstructured
                        ? unstructuredQ.data
                        : isStructuredDocument
                          ? ipMappingQ.data
                          : isWorkOrders
                            ? workOrdersQ.data
                            : isTagImport
                              ? tagImportQ.data
                              : isTabularDump
                                ? tabularDumpQ.data
                                : isSql
                                  ? sqlQ.data
                                  : isSoap
                                    ? soapQ.data
                                    : isPss
                                      ? pssQ.data
                                      : isCanary
                                        ? canaryQ.data
                                        : null
  const isLoading =
    structuredQ.isLoading ||
    plcQ.isLoading ||
    roboticsQ.isLoading ||
    visionQ.isLoading ||
    mtconnectQ.isLoading ||
    piQ.isLoading ||
    canaryQ.isLoading ||
    experionQ.isLoading ||
    srsQ.isLoading ||
    safetyManagerQ.isLoading ||
    tagMappingQ.isLoading ||
    unstructuredQ.isLoading ||
    ipMappingQ.isLoading ||
    workOrdersQ.isLoading ||
    tagImportQ.isLoading ||
    tabularDumpQ.isLoading ||
    sqlQ.isLoading ||
    soapQ.isLoading ||
    pssQ.isLoading

  const [applyStructured, applyState] = useApplyStructuredDiffMutation()
  const [discardStructuredDiff, discardState] = useDiscardStructuredDiffMutation()
  const [getTagImportErrorCsv] = useLazyGetTagImportErrorCsvQuery()
  const [diffActionError, setDiffActionError] = React.useState<string | null>(null)
  const [tagImportActionError, setTagImportActionError] = React.useState<string | null>(null)
  const branchGuard = useProtectedBranchGuard(useActiveBranchRecord())

  const isDiffPending =
    isScada &&
    job !== undefined &&
    job?.state === 'done' &&
    'ingestMode' in (job ?? {}) &&
    (job as { ingestMode?: string }).ingestMode === 'diff-pending-apply'

  const handleApplyDiff = () => {
    if (!isScada || !job) return
    if (!branchGuard.ensureWritable()) return
    const structured = job as typeof structuredQ.data
    if (!structured?.diffId || !structured.siteId || !structured.gatewayId) {
      setDiffActionError(
        'This diff is no longer cached on the server. Re-upload the file to regenerate the preview.'
      )
      return
    }
    setDiffActionError(null)
    // Fire-and-forget: the server transitions the job through processing →
    // done over several seconds. Awaiting would make the panel feel frozen;
    // the job's wake-ups surface each state change as it lands instead.
    // Errors land via the catch and surface as an inline alert.
    void applyStructured({
      diffId: structured.diffId,
      siteId: structured.siteId,
      gatewayId: structured.gatewayId,
      // Used by the mutation's onQueryStarted to optimistically flip the
      // job's cached state to 'processing' so the row's status pill stops
      // reading "review" the moment the user clicks Apply.
      jobId: structured.id
    })
      .unwrap()
      .catch((err: unknown) => {
        setDiffActionError(`Apply failed: ${extractErrorMessage(err)}`)
      })
  }

  const handleDiscardDiff = async () => {
    if (!job) return
    setDiffActionError(null)
    try {
      await discardStructuredDiff(job.id).unwrap()
    } catch (err: unknown) {
      setDiffActionError(`Discard failed: ${extractErrorMessage(err)}`)
    }
  }

  const isDiffActioning = applyState.isLoading || discardState.isLoading
  const tagImportJob = isTagImport ? (job as TagImportJob | undefined) : undefined
  const tagImportPreview = tagImportJob?.payload?.preview
  const dumpJob = isTabularDump ? (job as TabularDumpJob | undefined) : undefined
  const dumpPreview = dumpJob?.payload?.preview

  const handleTagImportErrorDownload = () => {
    if (!tagImportJob) return
    setTagImportActionError(null)
    void getTagImportErrorCsv(tagImportJob.id)
      .unwrap()
      .then(downloadTagImportErrors)
      .catch((error: unknown) =>
        setTagImportActionError(`Could not download errors: ${extractErrorMessage(error)}`)
      )
  }

  const hasDiffData =
    isScada &&
    job?.state === 'done' &&
    'ingestMode' in (job ?? {}) &&
    ((job as { ingestMode?: string }).ingestMode === 'diff' ||
      (job as { ingestMode?: string }).ingestMode === 'diff-pending-apply')

  const [showDiffOperations, setShowDiffOperations] = React.useState(false)
  // Default the logs accordion to open for IP-mapping / Work Orders rows: their
  // agent summary / tone-guide save log IS the deliverable for those jobs, so
  // it should be visible without a click. SCADA / PLC /
  // Unstructured rows have richer detail (diffs, doc tables) so their
  // logs stay collapsed unless the user opens them.
  const [showLogsByDefault] = React.useState(
    isStructuredDocument ||
      isMTConnect ||
      isPI ||
      isCanary ||
      isExperion ||
      isSrs ||
      isSafetyManager ||
      isTagMapping ||
      isSoap ||
      isPss ||
      isWorkOrders ||
      isUpdatePlc
  )
  const [logsOpen, setLogsOpen] = React.useState(showLogsByDefault)

  const jobLogCount = job?.logCount ?? job?.logs.length ?? 0
  const tagRegistration = isScada ? job?.result?.tagRegistration : undefined
  const diffPreviewQ = useGetJobDiffPreviewQuery(id, {
    skip: !showDiffOperations || !hasDiffData
  })

  if (!job) {
    // Render the same outer wrapper as the loaded state so the row's
    // accordion animation targets a stable height. Without this, the
    // grid-rows transition first interpolates toward a tiny "Loading…"
    // line, then re-targets when data arrives — visible as a juddery
    // height jump and background-color flash, especially for Document
    // panels whose loaded content has extra grid rows.
    return (
      <div className="flex flex-col gap-4 bg-bg-sunk px-4 py-4 min-h-40">
        <div className="flex items-center gap-2 text-sm text-ink-3">
          {isLoading ? <Loader2 className="size-4 animate-spin" /> : null}
          {isLoading ? 'Loading job detail…' : 'No detail available.'}
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 bg-bg-sunk px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Tag import auto-registers after preview — no status/review pill. */}
        {!isTagImport ? (
          <div className="flex items-center gap-2">
            <StatusPill status={isDiffPending ? 'warn' : statusToPill[job.state as IngestJobState]}>
              {isDiffPending ? 'review needed' : statusToLabel[job.state as IngestJobState]}
            </StatusPill>
            {isUnstructured ? (
              <Button asChild size="sm" variant="outline">
                <Link to={ingestFullSessionPath(job.id)} state={INGEST_SESSION_FOCUS_STATE}>
                  Open ingest session
                </Link>
              </Button>
            ) : null}
          </div>
        ) : null}
        {isDiffPending ? (
          <div className="flex flex-row gap-2">
            <Button
              size="sm"
              className="bg-st-down-bg text-st-down hover:bg-st-down-bg/70"
              disabled={isDiffActioning}
              onClick={() => void handleDiscardDiff()}
            >
              {discardState.isLoading ? <Loader2 className="animate-spin" /> : null}
              Discard
            </Button>
            <Button
              size="sm"
              className="bg-st-run-bg text-st-run hover:bg-st-run-bg/70"
              disabled={isDiffActioning}
              onClick={() => void handleApplyDiff()}
            >
              {applyState.isLoading ? <Loader2 className="animate-spin" /> : null}
              Apply diff
            </Button>
          </div>
        ) : null}
      </div>

      {diffActionError ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-sm text-st-down">
          {diffActionError}
        </div>
      ) : null}
      {tagImportActionError ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-sm text-st-down">
          {tagImportActionError}
        </div>
      ) : null}

      {tagRegistration ? (
        <div
          role={tagRegistration.fallbackReason ? 'alert' : 'status'}
          className={cn(
            'rounded-md border px-3 py-2 text-xs',
            tagRegistration.fallbackReason
              ? 'border-st-warn/30 bg-st-warn-bg text-st-warn'
              : 'border-line bg-bg-elev text-ink-2'
          )}
        >
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">Live tag reads</span>
            <StatusPill status={tagRegistration.fallbackReason ? 'warn' : 'info'}>
              {tagRegistration.effectiveProtocol === 'webdev' ? 'WebDev' : 'OPC UA'}
            </StatusPill>
          </div>
          <p className="mt-1">
            {tagRegistration.fallbackReason
              ? 'OPC UA was requested, but validation failed. WebDev was used instead.'
              : `This ingest registered live tag reads through ${
                  tagRegistration.effectiveProtocol === 'webdev' ? 'WebDev' : 'OPC UA'
                }.`}
          </p>
          {tagRegistration.fallbackReason ? (
            <p className="mt-1 text-ink-3">{tagRegistration.fallbackReason}</p>
          ) : null}
        </div>
      ) : null}
      {tagImportPreview ? (
        <TagImportPreviewPanel
          preview={tagImportPreview}
          onDownloadErrors={handleTagImportErrorDownload}
        />
      ) : null}
      {dumpPreview && dumpPreview.length > 0 ? <DumpPreviewPanel tables={dumpPreview} /> : null}

      <div className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-1 text-xs">
        <div className="text-ink-3">File</div>
        <div className="font-mono text-ink break-all">{job.fileName}</div>

        <div className="text-ink-3">Submitted</div>
        <div className="text-ink-2">{new Date(job.submittedAt).toLocaleString()}</div>

        <div className="text-ink-3">Updated</div>
        <div className="text-ink-2">{new Date(job.updatedAt).toLocaleString()}</div>
      </div>

      {hasDiffData && (
        <div className="overflow-hidden rounded-md border border-line bg-bg-elev">
          {/* Whole summary acts as the expand toggle — click anywhere on it
              to reveal the operations table. */}
          <button
            type="button"
            onClick={() => setShowDiffOperations((v) => !v)}
            className="flex w-full cursor-pointer items-start justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-bg-sunk"
            aria-expanded={showDiffOperations}
          >
            <div className="flex flex-col gap-1">
              <div className="text-xs font-semibold text-ink">
                {(job as { ingestMode?: string }).ingestMode === 'diff-pending-apply'
                  ? 'Diff preview (apply pending)'
                  : 'Diff summary'}
              </div>
              <div className="flex flex-row flex-wrap gap-x-4 gap-y-1 text-xs">
                <span className="text-st-run">
                  +{(job as { result?: { nodesCreated?: number } }).result?.nodesCreated ?? 0}{' '}
                  created
                </span>
                <span className="text-st-warn">
                  ~{(job as { result?: { nodesModified?: number } }).result?.nodesModified ?? 0}{' '}
                  modified
                </span>
                <span className="text-st-down">
                  −{(job as { result?: { nodesDeleted?: number } }).result?.nodesDeleted ?? 0}{' '}
                  deleted
                </span>
                <span className="text-ink-3">
                  {(job as { result?: { relationshipsCreated?: number } }).result
                    ?.relationshipsCreated ?? 0}{' '}
                  rels created
                </span>
                <span className="text-ink-3">
                  {(job as { result?: { relationshipsDeleted?: number } }).result
                    ?.relationshipsDeleted ?? 0}{' '}
                  rels deleted
                </span>
                {(job as { result?: { udtRemap?: { instances: number } } }).result?.udtRemap ? (
                  <span className="text-ink-2">
                    {
                      (job as { result?: { udtRemap?: { instances: number } } }).result?.udtRemap
                        ?.instances
                    }{' '}
                    UDT instances re-mapped
                  </span>
                ) : null}
              </div>
            </div>
            <ChevronDown
              className={cn(
                'mt-0.5 size-4 shrink-0 text-ink-3 transition-transform duration-200 ease-out',
                showDiffOperations && 'rotate-180'
              )}
            />
          </button>
          {/* Smooth expand via grid-template-rows. Inner content stays mounted
              so the close animation has something to fade out. */}
          <div
            data-open={showDiffOperations}
            className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out data-[open=true]:grid-rows-[1fr]"
          >
            <div className="overflow-hidden">
              <div className="border-t border-line px-3 py-3">
                {diffPreviewQ.isLoading && !diffPreviewQ.data ? (
                  <div className="flex items-center gap-2 text-xs text-ink-3">
                    <Loader2 className="size-3 animate-spin" />
                    Loading operations…
                  </div>
                ) : diffPreviewQ.error ? (
                  <div
                    role="alert"
                    className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down"
                  >
                    Diff operations are no longer cached on the server. Re-upload the file to
                    regenerate the preview.
                  </div>
                ) : diffPreviewQ.data ? (
                  <DiffResultView result={diffPreviewQ.data} />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {job.error ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-sm text-st-down">
          {job.error}
        </div>
      ) : null}

      {debugMode ? (
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => setLogsOpen((o) => !o)}
            aria-expanded={logsOpen}
            className="flex w-fit cursor-pointer items-center gap-1 text-xs font-semibold text-ink-2 hover:text-ink"
          >
            <ChevronRight
              className={cn(
                'size-3 text-ink-3 transition-transform duration-200 ease-out motion-reduce:transition-none',
                logsOpen && 'rotate-90'
              )}
            />
            Job logs
            {jobLogCount ? <span className="font-normal text-ink-3">({jobLogCount})</span> : null}
          </button>
          <div
            data-open={logsOpen}
            className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none data-[open=true]:grid-rows-[1fr]"
          >
            <div className="overflow-hidden">
              <div
                data-open={logsOpen}
                className="flex flex-col gap-1.5 pt-1.5 opacity-0 transition-opacity duration-200 ease-out motion-reduce:transition-none data-[open=true]:opacity-100"
              >
                <JobLogs jobId={id} />
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {branchGuard.dialog}
    </div>
  )
}
